/**
 * Product Pricing / Nutritional Profile Types
 *
 * Zod schemas and types for Nutritions, Vitamins, Minerals, and Amino Acids
 * steps of the Add Product form.
 */
import { z } from 'zod';
import { optionalDecimal, optionalString } from './product-basic-info';

// -- Nutritions -------------------------------------------------

// Caloric Information
export const CaloricInformationSchema = z.object({
  displacement_for_pwd: optionalDecimal,
  osmolality: optionalDecimal,
  osmolarity: optionalDecimal,
  prsl: optionalDecimal,
  renal_solute_load: optionalDecimal,
  volume_to_provide_100_kcal: optionalDecimal,
  water: optionalDecimal,
});

// Protein
export const ProteinSchema = z.object({
  protein: optionalDecimal,
  protein_source: optionalString,
  protein_hydrolysis: optionalString,
  bcaa: optionalDecimal,
  total_n2: optionalDecimal,
});

// Fat
export const FatSchema = z.object({
  fat: optionalDecimal,
  mct: optionalDecimal,
  ala: optionalDecimal,
  linoleic_acid_omega_6: optionalDecimal,
  linolenic_acid_omega_3: optionalDecimal,
  ara: optionalDecimal,
  dha: optionalDecimal,
  epa: optionalDecimal,
  cholesterol: optionalDecimal,
});

// Carbohydrates
export const CarbohydratesSchema = z.object({
  cho: optionalDecimal,
  cho_source: optionalString,
  added_sugars: optionalDecimal,
  total_sugars: optionalDecimal,
  dietary_fiber: optionalDecimal,
  soluble_fiber: optionalDecimal,
  insoluble_fiber: optionalDecimal,
  prebiotic_fiber: optionalDecimal,
  dextrose: optionalDecimal,
  d_galactose: optionalDecimal,
});

// Serving Information
export const ServingInformationSchema = z.object({
  denominator_property: optionalString,
  denominator_volume: optionalString,
  recipe_dilution_for_reconstitution: optionalString,
  one_level_unpacked_cup: optionalDecimal,
  one_level_unpacked_teaspoon: optionalDecimal,
  one_unpacked_level_scoop: optionalDecimal,
});

// Ingredients
export const IngredientsSchema = z.object({
  full_ingredients_list: optionalString,
});

// Dietary Information
export const DietaryInformationSchema = z.object({
  gluten_free: optionalString,
  halal: optionalString,
  kosher: optionalString,
});

export const NutritionsSchema = z.object({
  caloric_information: CaloricInformationSchema,
  protein: ProteinSchema,
  fat: FatSchema,
  carbohydrates: CarbohydratesSchema,
  serving_information: ServingInformationSchema,
  ingredients: IngredientsSchema,
  dietary_information: DietaryInformationSchema,
});

export type NutritionsFormValues = z.infer<typeof NutritionsSchema>;

// -- Vitamins ---------------------------------------------------

export const BVitaminsSchema = z.object({
  biotin: optionalDecimal,
  folate: optionalDecimal,
  folic_acid: optionalDecimal,
  niacin: optionalDecimal,
  pantothenic_acid: optionalDecimal,
  riboflavin: optionalDecimal,
  thiamin: optionalDecimal,
  vitamin_b12: optionalDecimal,
  vitamin_b6: optionalDecimal,
});

export const FatSolubleVitaminsSchema = z.object({
  vitamin_a_iu: optionalDecimal,
  vitamin_a_rae: optionalDecimal,
  vitamin_d_iu: optionalDecimal,
  vitamin_d_mcg: optionalDecimal,
  vitamin_e_iu: optionalDecimal,
  vitamin_e_mg: optionalDecimal,
  vitamin_k: optionalDecimal,
});

export const OtherVitaminsSchema = z.object({
  choline: optionalDecimal,
  inositol: optionalDecimal,
  vitamin_c: optionalDecimal,
});

export const VitaminsSchema = z.object({
  b_vitamins: BVitaminsSchema,
  fat_soluble_vitamins: FatSolubleVitaminsSchema,
  other_vitamins_related: OtherVitaminsSchema,
});

export type VitaminsFormValues = z.infer<typeof VitaminsSchema>;

// -- Minerals ---------------------------------------------------

export const ElectrolytesSchema = z.object({
  acetate: optionalDecimal,
  chloride_meq: optionalDecimal,
  chloride_mg: optionalDecimal,
  potassium_meq: optionalDecimal,
  potassium_mg: optionalDecimal,
  sodium_meq: optionalDecimal,
  sodium_mg: optionalDecimal,
});

export const MajorMineralsSchema = z.object({
  calcium_meq: optionalDecimal,
  calcium_mg: optionalDecimal,
  magnesium_meq: optionalDecimal,
  magnesium_mg: optionalDecimal,
  phosphorus_mmol: optionalDecimal,
  phosphorus_mg: optionalDecimal,
});

export const TraceMineralsSchema = z.object({
  aluminum: optionalDecimal,
  chromium: optionalDecimal,
  copper: optionalDecimal,
  fluoride: optionalDecimal,
  iodine: optionalDecimal,
  iron: optionalDecimal,
  manganese: optionalDecimal,
  molybdenum: optionalDecimal,
  selenium: optionalDecimal,
  zinc: optionalDecimal,
});

export const MineralsSchema = z.object({
  electrolytes: ElectrolytesSchema,
  major_minerals: MajorMineralsSchema,
  trace_minerals: TraceMineralsSchema,
});

export type MineralsFormValues = z.infer<typeof MineralsSchema>;

// -- Amino Acids ------------------------------------------------

export const AminoAcidDerivativesSchema = z.object({
  carnitine: optionalDecimal,
  taurine: optionalDecimal,
});

export const EssentialAminoAcidsSchema = z.object({
  histidine: optionalDecimal,
  isoleucine: optionalDecimal,
  leucine: optionalDecimal,
  lysine: optionalDecimal,
  methionine: optionalDecimal,
  phenylalanine: optionalDecimal,
  threonine: optionalDecimal,
  tryptophan: optionalDecimal,
  valine: optionalDecimal,
});

export const NonEssentialAminoAcidsSchema = z.object({
  alanine: optionalDecimal,
  arginine: optionalDecimal,
  cystine: optionalDecimal,
  glycine: optionalDecimal,
  l_aspartic_acid: optionalDecimal,
  l_glutamic_acid: optionalDecimal,
  l_glutamine: optionalDecimal,
  proline: optionalDecimal,
  serine: optionalDecimal,
  tyrosine: optionalDecimal,
});

export const AminoAcidsSchema = z.object({
  amino_acid_derivatives: AminoAcidDerivativesSchema,
  essential_amino_acids: EssentialAminoAcidsSchema,
  non_essential_amino_acids: NonEssentialAminoAcidsSchema,
});

export type AminoAcidsFormValues = z.infer<typeof AminoAcidsSchema>;
